import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Settings, Sparkles } from 'lucide-react';

export interface NavTab {
  id: string;
  label: string;
  icon: React.ElementType;
}

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  tabs?: NavTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

export default function Layout({ children, title, tabs, activeTab, onTabChange }: LayoutProps) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const hasNav = tabs && tabs.length > 0 && activeTab && onTabChange;

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col md:flex-row">
      {/* --- Desktop Sidebar --- */}
      {hasNav && (
        <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-30">
          <div className="p-6">
            <div className="flex items-center space-x-2 text-blue-600 mb-1">
              <Sparkles size={24} className="animate-pulse" />
              <span className="font-bold text-xl tracking-tight">SSF Tracker</span>
            </div>
            <p className="text-xs text-slate-500 font-medium ml-8 uppercase tracking-widest">{isAdmin ? 'Admin Portal' : 'Convener Portal'}</p>
          </div>
          
          <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto mt-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                    isActive 
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon size={20} className={isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-600 transition-colors'} />
                  <span className="font-semibold text-sm">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
      )}

      {/* --- Main Content Area --- */}
      <div className={`flex-1 flex flex-col min-h-screen w-full ${hasNav ? 'md:pl-64' : ''} ${hasNav ? 'pb-20 md:pb-0' : ''}`}>
        
        {/* Top Header */}
        <header className="bg-white sticky top-0 z-20 border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 sm:px-8 h-16">
            {/* Title */}
            <div className="flex items-center min-w-0">
              <h1 className="text-lg sm:text-2xl font-extrabold text-slate-900 tracking-tight truncate">
                {title}
              </h1>
            </div>

            {/* Profile & Actions */}
            <div className="flex items-center space-x-3 shrink-0">
              {isAdmin && !window.location.pathname.includes('/admin') && (
                <button
                  onClick={() => navigate('/admin')}
                  className="hidden sm:flex items-center space-x-1.5 h-10 px-4 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-xl text-sm font-bold transition whitespace-nowrap border border-purple-200 shadow-sm"
                >
                  <Settings size={16} />
                  <span>Admin Mode</span>
                </button>
              )}
              
              <div className="flex items-center space-x-3 px-1.5 py-1.5 bg-slate-100/80 rounded-2xl border border-slate-200/50">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : <User size={16} />}
                </div>
                <div className="hidden sm:block text-left pr-3">
                  <p className="text-sm font-bold text-slate-900 leading-none">{user?.name}</p>
                  <p className="text-[10px] font-semibold text-slate-500 mt-1 leading-none uppercase tracking-wider">{user?.role}</p>
                </div>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-10 h-10 sm:w-auto sm:px-4 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl transition shadow-sm bg-white border border-slate-200"
                title="Logout"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline ml-2 font-bold text-sm">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8 animate-in fade-in duration-300">
          {children}
        </main>
      </div>

      {/* --- Mobile Bottom Navigation --- */}
      {hasNav && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur-lg border-t border-slate-200 flex items-center justify-around pb-safe pt-2 px-2 z-40 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className="flex flex-col items-center justify-center w-16 h-14"
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 ${isActive ? 'bg-blue-100 text-blue-600 scale-110' : 'text-slate-500'}`}>
                  <Icon size={isActive ? 20 : 22} className={isActive ? 'font-bold' : ''} />
                </div>
                <span className={`text-[10px] mt-1 font-semibold transition-colors duration-300 ${isActive ? 'text-blue-600' : 'text-slate-500'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
